<script setup lang="ts">
import { computed, useAttrs } from 'vue'

defineOptions({
  inheritAttrs: false,
})

// Import only the icons you actually need
import {
  Brain,
  Timer,
  Hash,
  Calendar,
  Clock,
  CalendarClock,
  Map,
  Accessibility,
  SlidersHorizontal,
  ShieldCheck,
  UserX,
  Cookie,
  Mail,
  PhoneCall,
  Stethoscope,
  Lightbulb,
  Globe,
  TriangleAlert,
  ArrowRight,
  ArrowUpRight,
  Info,
  Split,
  ClipboardClock,
  ClipboardList,
  X,
  Check,
  Palette,
  Plus,
  Sparkles,
  ChartSpline,
  ChartBarDecreasing,
  FileXCorner,
  ExternalLink,
  Eye,
  Cpu,
  HeartPulse,
  CloudRain,
  Smile,
  Wind,
  Users,
  Briefcase,
  House,
  Activity,
  BookOpen,
  CircleCheckBig,
  Shredder,
  Utensils,
  ShieldAlert,
  Zap,
  Flame,
  Battery,
  Waves,
  Moon,
  Frown,
  Target,
  Dices,
  Smartphone,
  Gauge,
  Compass,
  MessageCircle,
  Sun,
  Hand,
  Leaf,
  Repeat,
  Trash2,
  Microscope,
  Mic,
  MicOff,
  Send,
  Wifi,
} from 'lucide-vue-next'

// Map the icons you support
const ICONS = {
  Brain,
  Timer,
  Hash,
  Calendar,
  Clock,
  CalendarClock,
  Map,
  Accessibility,
  SlidersHorizontal,
  ShieldCheck,
  UserX,
  Cookie,
  Mail,
  PhoneCall,
  Stethoscope,
  Lightbulb,
  Globe,
  TriangleAlert,
  ArrowRight,
  ArrowUpRight,
  Info,
  Split,
  ClipboardClock,
  ClipboardList,
  X,
  Check,
  Palette,
  Plus,
  Sparkles,
  ChartSpline,
  ChartBarDecreasing,
  FileXCorner,
  ExternalLink,
  Eye,
  Cpu,
  HeartPulse,
  CloudRain,
  Smile,
  Wind,
  Users,
  Briefcase,
  House,
  Activity,
  BookOpen,
  CircleCheckBig,
  Shredder,
  Utensils,
  ShieldAlert,
  Zap,
  Flame,
  Battery,
  Waves,
  Moon,
  Frown,
  Target,
  Dices,
  Smartphone,
  Gauge,
  Compass,
  MessageCircle,
  Sun,
  Hand,
  Leaf,
  Repeat,
  Trash2,
  Microscope,
  Mic,
  MicOff,
  Send,
  Wifi,
} as const

const ICON_ALIASES: Record<string, keyof typeof ICONS> = {
  'arrow-right': 'ArrowRight',
  arrowright: 'ArrowRight',
  'arrow-up-right': 'ArrowUpRight',
  arrowupright: 'ArrowUpRight',
  brain: 'Brain',
  check: 'Check',
  chec: 'Check',
  'clipboard-clock': 'ClipboardClock',
  clipboardclock: 'ClipboardClock',
  'clipboard-list': 'ClipboardList',
  clipboardlist: 'ClipboardList',
  'chart-spline': 'ChartSpline',
  chartspline: 'ChartSpline',
  'chart-bar-decreasing': 'ChartBarDecreasing',
  chartbardecreasing: 'ChartBarDecreasing',
  clock: 'Clock',
  cookie: 'Cookie',
  globe: 'Globe',
  info: 'Info',
  lightbulb: 'Lightbulb',
  mail: 'Mail',
  palette: 'Palette',
  plus: 'Plus',
  'triangle-alert': 'TriangleAlert',
  trianglealert: 'TriangleAlert',
  phone: 'PhoneCall',
  'phone-call': 'PhoneCall',
  sparkles: 'Sparkles',
  shieldcheck: 'ShieldCheck',
  'shield-check': 'ShieldCheck',
  split: 'Split',
  stethoscope: 'Stethoscope',
  'file-x-corner': 'FileXCorner',
  filexcorner: 'FileXCorner',
  external: 'ExternalLink',
  'external-link': 'ExternalLink',
  externallink: 'ExternalLink',
  eye: 'Eye',
  cpu: 'Cpu',
  'heart-pulse': 'HeartPulse',
  heartpulse: 'HeartPulse',
  'cloud-rain': 'CloudRain',
  cloudrain: 'CloudRain',
  smile: 'Smile',
  wind: 'Wind',
  users: 'Users',
  hash: 'Hash',
  calendar: 'Calendar',
  'calendar-clock': 'CalendarClock',
  calendarclock: 'CalendarClock',
  map: 'Map',
  accessibility: 'Accessibility',
  'sliders-horizontal': 'SlidersHorizontal',
  slidershorizontal: 'SlidersHorizontal',
  briefcase: 'Briefcase',
  house: 'House',
  home: 'House',
  activity: 'Activity',
  'book-open': 'BookOpen',
  bookopen: 'BookOpen',
  'circle-check-big': 'CircleCheckBig',
  circlecheckbig: 'CircleCheckBig',
  shredder: 'Shredder',
  'shield-alert': 'ShieldAlert',
  shieldalert: 'ShieldAlert',
  zap: 'Zap',
  flame: 'Flame',
  battery: 'Battery',
  waves: 'Waves',
  moon: 'Moon',
  frown: 'Frown',
  target: 'Target',
  dices: 'Dices',
  smartphone: 'Smartphone',
  gauge: 'Gauge',
  compass: 'Compass',
  'message-circle': 'MessageCircle',
  messagecircle: 'MessageCircle',
  sun: 'Sun',
  hand: 'Hand',
  leaf: 'Leaf',
  repeat: 'Repeat',
  'trash-2': 'Trash2',
  trash2: 'Trash2',
  microscope: 'Microscope',
  mic: 'Mic',
  'mic-off': 'MicOff',
  micoff: 'MicOff',
  send: 'Send',
  wifi: 'Wifi',
  timer: 'Timer',
  utensils: 'Utensils',
  x: 'X',
  userx: 'UserX',
  'user-x': 'UserX',
}

type Props = {
  name: string
  size?: number
  strokeWidth?: number
}

const props = withDefaults(defineProps<Props>(), {
  size: 24,
  strokeWidth: 2,
})

const attrs = useAttrs()

const sizeAttrs = computed<Record<string, unknown>>(() => ({
  size: hasSizeClass(attrs.class) ? null : props.size,
}))
const componentAttrs = computed(() => ({
  ...sizeAttrs.value,
  ...attrs,
}))

const resolvedIconName = computed(() => {
  const trimmed = props.name.trim()
  if (!trimmed) return 'TriangleAlert'

  return ICON_ALIASES[trimmed.toLowerCase()] ?? trimmed
})

const component = computed(() => {
  const iconName = resolvedIconName.value

  if (iconName in ICONS) {
    return ICONS[iconName]
  }

  return ICONS.TriangleAlert
})

function hasSizeClass(value: unknown): boolean {
  if (!value) {
    return false
  }

  if (typeof value === 'string') {
    return /(?:^|\s)(?:size-|w-|h-)/.test(value)
  }

  if (Array.isArray(value)) {
    return value.some((item) => hasSizeClass(item))
  }

  if (typeof value === 'object') {
    return Object.entries(value as Record<string, unknown>)
      .some(([className, enabled]) => Boolean(enabled) && hasSizeClass(className))
  }

  return false
}
</script>

<template>
  <component
    :is="component"
    :stroke-width="strokeWidth"
    aria-hidden="true"
    v-bind="componentAttrs"
  />
</template>
